/**
 * Central asset map for the MusclePharm India site.
 * All assets live in /app/frontend/public/assets/ (served at /assets/*).
 *
 * Missing assets fall back gracefully to the closest available image so the
 * layout stays intact.
 */
const BASE = "/assets";

// ---------- Logos ----------
export const LOGOS = {
  musclepharm:      `${BASE}/musclepharm-logo.png`,
  musclepharmWhite: `${BASE}/musclepharm-logo-white.png`,
  footer:           `${BASE}/musclepharm-logo-white.png`,
  musclePro:        `${BASE}/muscle-pro-nutrition-logo.png`,
  nutriride:        `${BASE}/nutriride-logo.svg`,
  adsGallery:       `${BASE}/ads-gallery-logo.svg`,
};

// ---------- Product-benefit banners (also used as page-level banners) ----------
export const BENEFIT_BANNERS = {
  "combat-whey":       `${BASE}/product-benefit-banner-combat-whey.jpg`,
  "combat-preworkout": `${BASE}/product-benefit-banner-combat-preworkout.jpg`,
};

// ---------- Homepage hero slides (fallbacks: benefit banners) ----------
export const HERO_BANNERS = {
  slide1: `${BASE}/product-benefit-banner-combat-whey.jpg`,           // Brand / Combat Range
  slide2: `${BASE}/product-benefit-banner-combat-whey.jpg`,           // Combat 100% Whey
  slide3: `${BASE}/product-benefit-banner-combat-preworkout.jpg`,     // Combat Preworkout
};

// ---------- Page banners (About / Authenticity / Contact) — fallbacks ----------
export const PAGE_BANNERS = {
  about:         `${BASE}/product-benefit-banner-combat-whey.jpg`,
  aboutIndia:    `${BASE}/product-benefit-banner-combat-preworkout.jpg`,
  authenticity:  `${BASE}/product-benefit-banner-combat-whey.jpg`,
  contact:       `${BASE}/product-benefit-banner-combat-preworkout.jpg`,
  brandStory:    `${BASE}/product-hero-combat-whey-chocolate-milk.jpg`,
  testimonialBg: `${BASE}/product-benefit-banner-combat-preworkout.jpg`,
};

// ---------- Flavour banner tiles ----------
export const FLAVOUR_BANNERS = {
  "chocolate-milk":    `${BASE}/flavour-banner-chocolate-milk.jpg`,
  "smooth-cappuccino": `${BASE}/flavour-banner-smooth-cappuccino.jpg`,
  "strawberry-cream":  `${BASE}/flavour-banner-strawberry-cream.jpg`,
  "watermelon":        `${BASE}/flavour-banner-watermelon.jpg`,
  "fruit-punch":       `${BASE}/flavour-banner-fruit-punch.jpg`,
  "melon-hwachae":     `${BASE}/flavour-banner-melon-hwachae.jpg`,
};

// ---------- Ingredient graphics ----------
// Whey has per-flavour graphics. Preworkout falls back to its benefit banner.
export const INGREDIENT_GRAPHICS = {
  "combat-whey": {
    "chocolate-milk":    `${BASE}/ingredient-section-graphic-combat-whey-chocolate-milk.jpg`,
    "smooth-cappuccino": `${BASE}/ingredient-section-graphic-combat-whey-smooth-cappuccino.jpg`,
    "strawberry-cream":  `${BASE}/ingredient-section-graphic-combat-whey-strawberry-cream.jpg`,
  },
  "combat-preworkout": {
    "watermelon":    `${BASE}/product-benefit-banner-combat-preworkout.jpg`,
    "fruit-punch":   `${BASE}/product-benefit-banner-combat-preworkout.jpg`,
    "melon-hwachae": `${BASE}/product-benefit-banner-combat-preworkout.jpg`,
  },
};

// ---------- Product hero (front of jar per flavour) ----------
export const PRODUCT_HEROES = {
  "combat-whey": {
    "chocolate-milk":    `${BASE}/product-hero-combat-whey-chocolate-milk.jpg`,
    "smooth-cappuccino": `${BASE}/product-hero-combat-whey-smooth-cappuccino.jpg`,
    "strawberry-cream":  `${BASE}/product-hero-combat-whey-strawberry-cream.jpg`,
  },
  "combat-preworkout": {
    "watermelon":    `${BASE}/product-hero-combat-preworkout-watermelon.jpg`,
    "fruit-punch":   `${BASE}/product-hero-combat-preworkout-fruit-punch.jpg`,
    "melon-hwachae": `${BASE}/product-hero-combat-preworkout-melon-hwachae.jpg`,
  },
};

// ---------- Product galleries per flavour ----------
// For whey we only have 02/03/04. We use the hero as "01" so PDP has 4 images.
export const PRODUCT_GALLERIES = {
  "combat-whey": {
    "chocolate-milk": [
      `${BASE}/product-hero-combat-whey-chocolate-milk.jpg`,
      `${BASE}/product-gallery-combat-whey-chocolate-milk-02.jpg`,
      `${BASE}/product-gallery-combat-whey-chocolate-milk-03.jpg`,
      `${BASE}/product-gallery-combat-whey-chocolate-milk-04.jpg`,
    ],
    "smooth-cappuccino": [
      `${BASE}/product-hero-combat-whey-smooth-cappuccino.jpg`,
      `${BASE}/product-gallery-combat-whey-smooth-cappuccino-02.jpg`,
      `${BASE}/product-gallery-combat-whey-smooth-cappuccino-03.jpg`,
      `${BASE}/product-gallery-combat-whey-smooth-cappuccino-04.jpg`,
    ],
    "strawberry-cream": [
      `${BASE}/product-hero-combat-whey-strawberry-cream.jpg`,
      `${BASE}/product-gallery-combat-whey-strawberry-cream-02.jpg`,
      `${BASE}/product-gallery-combat-whey-strawberry-cream-03.jpg`,
      `${BASE}/product-gallery-combat-whey-strawberry-cream-04.jpg`,
    ],
  },
  "combat-preworkout": {
    "watermelon": [
      `${BASE}/product-hero-combat-preworkout-watermelon.jpg`,
      `${BASE}/product-gallery-combat-preworkout-watermelon-02.jpg`,
      `${BASE}/product-gallery-combat-preworkout-watermelon-03.jpg`,
      `${BASE}/product-gallery-combat-preworkout-watermelon-04.jpg`,
    ],
    "fruit-punch": [
      `${BASE}/product-hero-combat-preworkout-fruit-punch.jpg`,
      `${BASE}/product-gallery-combat-preworkout-fruit-punch-02.jpg`,
      `${BASE}/product-gallery-combat-preworkout-fruit-punch-03.jpg`,
      `${BASE}/product-gallery-combat-preworkout-fruit-punch-04.jpg`,
    ],
    "melon-hwachae": [
      `${BASE}/product-hero-combat-preworkout-melon-hwachae.jpg`,
      `${BASE}/product-gallery-combat-preworkout-melon-hwachae-02.jpg`,
      `${BASE}/product-gallery-combat-preworkout-melon-hwachae-03.jpg`,
      `${BASE}/product-gallery-combat-preworkout-melon-hwachae-04.jpg`,
    ],
  },
};

// ---------- Product listing default image (used on Home / Products pages) ----------
export const PRODUCT_LISTING = {
  "combat-whey":       `${BASE}/product-listing-combat-whey-chocolate-milk-01.jpg`,
  "combat-preworkout": `${BASE}/product-listing-combat-preworkout-watermelon-01.jpg`,
};

// ---------- Helpers ----------
export const getHero = (slug, flavourId) =>
  PRODUCT_HEROES[slug]?.[flavourId] || PRODUCT_LISTING[slug];

export const getGallery = (slug, flavourId) =>
  PRODUCT_GALLERIES[slug]?.[flavourId] || [PRODUCT_LISTING[slug]];

export const getIngredientGraphic = (slug, flavourId) =>
  INGREDIENT_GRAPHICS[slug]?.[flavourId] || BENEFIT_BANNERS[slug];

export const getFlavourBanner = (flavourId) => FLAVOUR_BANNERS[flavourId];
